/**
 * Scoped Shadow DOM styles. Shipped as a constant string so the package has
 * zero runtime CSS dependencies and the host page can't leak styles in.
 * Every token that a customer might want to override is a CSS variable on
 * :host, with a hard fallback so the default always looks finished.
 */
export const styles = `
  :host {
    --hw-primary: #7c3aed;
    --hw-primary-rgb: 124, 58, 237;
    --hw-radius: 16px;
    --hw-font: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --hw-bg: #09090b;
    --hw-surface: rgba(24, 24, 27, 0.95);
    --hw-surface-hover: rgba(39, 39, 42, 0.95);
    --hw-border: rgba(63, 63, 70, 0.8);
    --hw-text: #fafafa;
    --hw-text-muted: #a1a1aa;
    --hw-text-subtle: #71717a;
    --hw-input-bg: #18181b;
    --hw-input-text: #fafafa;
    --hw-input-placeholder: #52525b;
    --hw-danger: #ef4444;
    --hw-success: #10b981;
    --hw-warning: #f59e0b;
    display: block;
    height: 100%;
    width: 100%;
    font-family: var(--hw-font);
    color: var(--hw-text);
    background: var(--hw-bg);
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
  }
  :host([data-theme="light"]) {
    --hw-bg: #ffffff;
    --hw-surface: rgba(244, 244, 245, 0.95);
    --hw-surface-hover: rgba(228, 228, 231, 0.95);
    --hw-border: rgba(228, 228, 231, 1);
    --hw-text: #18181b;
    --hw-text-muted: #52525b;
    --hw-text-subtle: #a1a1aa;
    --hw-input-bg: #ffffff;
    --hw-input-text: #18181b;
    --hw-input-placeholder: #a1a1aa;
  }
  * { box-sizing: border-box; }
  button { font: inherit; color: inherit; background: none; border: none; cursor: pointer; padding: 0; }
  a { color: var(--hw-primary); text-decoration: none; }
  a:hover { text-decoration: underline; }
  .shell {
    display: flex; flex-direction: column; height: 100%;
    background: var(--hw-bg);
    overflow: hidden;
  }
  .header {
    flex-shrink: 0;
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--hw-border);
    background: var(--hw-surface);
    backdrop-filter: blur(8px);
  }
  .avatar {
    width: 36px; height: 36px; border-radius: 10px;
    background: rgba(var(--hw-primary-rgb), 0.13);
    border: 1px solid rgba(var(--hw-primary-rgb), 0.3);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; flex-shrink: 0;
  }
  .avatar img { width: 100%; height: 100%; object-fit: cover; }
  .avatar svg { width: 18px; height: 18px; color: var(--hw-primary); }
  .header-text { flex: 1; min-width: 0; }
  .title { font-size: 13px; font-weight: 600; color: var(--hw-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .subtitle { font-size: 11px; color: var(--hw-text-subtle); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
  .header-btn {
    border: 1px solid var(--hw-border);
    background: var(--hw-input-bg);
    border-radius: 8px;
    font-size: 11px;
    color: var(--hw-text-muted);
    padding: 5px 10px;
    transition: all 0.15s;
  }
  .header-btn:hover { color: var(--hw-text); border-color: rgba(var(--hw-primary-rgb), 0.5); }

  .messages {
    flex: 1; overflow-y: auto; padding: 20px 16px;
  }
  .msg { max-width: 720px; margin: 0 auto 20px; }
  /* Column layout stacks attachments above the bubble; align-items: flex-end
     keeps everything right-aligned. Previously the bubble sat inside an
     inline-styled wrapper that ALSO had max-width: 80%, creating nested
     percentages that browsers resolve to degenerate widths — "hola" wrapped
     as "h / ol / a" and longer strings broke at arbitrary points. */
  .msg-row.user { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
  .bubble-user {
    max-width: 80%;
    width: fit-content;
    background: var(--hw-primary);
    color: white;
    padding: 10px 14px;
    border-radius: 16px 16px 4px 16px;
    font-size: 13px; line-height: 1.55;
    white-space: pre-wrap; overflow-wrap: break-word;
  }
  .msg-attachments { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; margin-bottom: 6px; }
  .msg-attach-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 11px;
  }
  .msg-attach-chip svg { width: 12px; height: 12px; }
  .msg-attach-chip .name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .assistant { color: var(--hw-text); font-size: 14px; line-height: 1.65; }
  .assistant p { margin: 0 0 8px; }
  .assistant p:last-child { margin-bottom: 0; }
  .assistant h1, .assistant h2, .assistant h3 { margin: 14px 0 6px; font-weight: 600; color: var(--hw-text); }
  .assistant h1 { font-size: 18px; }
  .assistant h2 { font-size: 16px; }
  .assistant h3 { font-size: 14px; }
  .assistant h1:first-child, .assistant h2:first-child, .assistant h3:first-child { margin-top: 0; }
  .assistant a {
    color: var(--hw-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-word;
  }
  .assistant a:hover { opacity: 0.85; }
  .assistant ul, .assistant ol { margin: 6px 0 8px; padding-left: 20px; }
  .assistant li { margin: 2px 0; }
  .assistant blockquote {
    margin: 8px 0;
    padding: 4px 12px;
    border-left: 3px solid rgba(var(--hw-primary-rgb), 0.5);
    color: var(--hw-text-muted);
    font-style: italic;
  }
  .assistant pre {
    background: var(--hw-input-bg);
    border: 1px solid var(--hw-border);
    border-radius: 10px;
    padding: 12px; font-size: 12px;
    overflow-x: auto; margin: 8px 0;
    font-family: ui-monospace, SFMono-Regular, 'Menlo', monospace;
  }
  .assistant code { background: rgba(var(--hw-primary-rgb), 0.1); color: var(--hw-primary); padding: 1px 6px; border-radius: 4px; font-size: 0.92em; font-family: ui-monospace, SFMono-Regular, 'Menlo', monospace; }
  .assistant pre code { background: transparent; color: inherit; padding: 0; }
  .assistant strong { font-weight: 600; color: var(--hw-text); }
  .assistant em { font-style: italic; }

  .tool-calls { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
  .tool-pill {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid; border-radius: 6px; padding: 2px 8px;
    font-size: 10px; font-family: ui-monospace, monospace;
  }
  .tool-pill.running { border-color: rgba(245, 158, 11, 0.3); background: rgba(245, 158, 11, 0.1); color: var(--hw-warning); }
  .tool-pill.done { border-color: rgba(16, 185, 129, 0.3); background: rgba(16, 185, 129, 0.1); color: var(--hw-success); }
  .tool-pill svg { width: 10px; height: 10px; }
  .tool-pill .spin { animation: hw-spin 1s linear infinite; }

  .typing { display: inline-flex; gap: 4px; align-items: center; height: 16px; }
  .typing span { width: 6px; height: 6px; border-radius: 999px; background: var(--hw-text-subtle); animation: hw-bounce 1.4s infinite; }
  .typing span:nth-child(2) { animation-delay: 0.15s; }
  .typing span:nth-child(3) { animation-delay: 0.3s; }
  .cursor { display: inline-block; width: 6px; height: 14px; margin-left: 2px; vertical-align: middle; background: var(--hw-primary); animation: hw-pulse 0.9s infinite; }

  .error-box {
    margin: 0 auto 14px; max-width: 720px;
    border-radius: 10px; padding: 10px 12px;
    background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3);
    color: var(--hw-danger); font-size: 12px;
  }

  .composer {
    flex-shrink: 0; border-top: 1px solid var(--hw-border); background: var(--hw-surface);
    backdrop-filter: blur(8px);
  }
  .composer-inner { max-width: 720px; margin: 0 auto; padding: 12px 16px 10px; }
  .composer-row { display: flex; gap: 8px; align-items: flex-end; }
  .textarea {
    flex: 1; resize: none; overflow: hidden;
    border-radius: 12px;
    border: 1px solid var(--hw-border);
    background: var(--hw-input-bg);
    color: var(--hw-input-text);
    padding: 11px 14px;
    font-size: 13px; font-family: inherit; line-height: 1.5;
    /* Match the 44px send/attach buttons so the row reads as one strip
       even before autoresize finishes laying out (some browsers report
       scrollHeight=0 on first paint inside a freshly-mounted iframe,
       which used to collapse the textarea below the placeholder). */
    min-height: 44px;
    max-height: 200px;
    outline: none;
    transition: border-color 0.15s;
  }
  .textarea::placeholder { color: var(--hw-input-placeholder); }
  .textarea:focus { border-color: var(--hw-primary); box-shadow: 0 0 0 1px var(--hw-primary); }
  .textarea:disabled { opacity: 0.6; cursor: not-allowed; }

  .btn-icon {
    flex-shrink: 0;
    width: 44px; height: 44px;
    border-radius: 12px; border: 1px solid var(--hw-border);
    background: var(--hw-input-bg);
    color: var(--hw-text-muted);
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .btn-icon:hover:not(:disabled) { color: var(--hw-text); border-color: rgba(var(--hw-primary-rgb), 0.4); }
  .btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-icon svg { width: 18px; height: 18px; }

  .btn-send {
    flex-shrink: 0;
    width: 44px; height: 44px; border-radius: 12px;
    background: var(--hw-primary);
    color: white;
    display: flex; align-items: center; justify-content: center;
    transition: opacity 0.15s, transform 0.15s;
  }
  .btn-send:hover:not(:disabled) { transform: translateY(-1px); }
  .btn-send:disabled { opacity: 0.3; cursor: not-allowed; }
  .btn-send.stop { background: var(--hw-danger); }
  .btn-send svg { width: 18px; height: 18px; }

  .attachments-strip { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
  .attach-chip {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid; border-radius: 999px;
    padding: 4px 10px; font-size: 11px;
    max-width: 100%;
  }
  .attach-chip.neutral { border-color: var(--hw-border); background: var(--hw-input-bg); color: var(--hw-text-muted); }
  .attach-chip.ok { border-color: rgba(16, 185, 129, 0.4); background: rgba(16, 185, 129, 0.1); color: var(--hw-success); }
  .attach-chip.bad { border-color: rgba(239, 68, 68, 0.4); background: rgba(239, 68, 68, 0.1); color: var(--hw-danger); }
  .attach-chip svg { width: 12px; height: 12px; }
  .attach-chip .spin { animation: hw-spin 1s linear infinite; }
  .attach-chip .name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .attach-chip .status { opacity: 0.75; }
  .attach-chip .remove { padding: 1px 3px; border-radius: 4px; display: inline-flex; }
  .attach-chip .remove:hover { background: rgba(0, 0, 0, 0.15); }

  .drop-overlay {
    position: absolute; inset: 0; z-index: 10;
    display: flex; align-items: center; justify-content: center;
    background: rgba(9, 9, 11, 0.75);
    backdrop-filter: blur(4px);
    pointer-events: none;
    border: 3px dashed var(--hw-primary);
    border-radius: var(--hw-radius);
  }
  :host([data-theme="light"]) .drop-overlay { background: rgba(255, 255, 255, 0.8); }
  .drop-overlay .drop-text { text-align: center; }
  .drop-overlay svg { width: 40px; height: 40px; color: var(--hw-primary); margin-bottom: 8px; }
  .drop-overlay p { margin: 0; font-weight: 600; color: var(--hw-primary); font-size: 14px; }

  .footer { text-align: center; font-size: 10px; color: var(--hw-text-subtle); padding: 4px 0 8px; }

  .boot-error {
    padding: 20px;
    color: var(--hw-danger);
    border: 1px solid rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.05);
    border-radius: 10px;
    margin: 16px;
    font-size: 13px;
  }
  .boot-error strong { display: block; margin-bottom: 4px; }

  .shell-wrap { position: relative; height: 100%; display: flex; flex-direction: column; }

  @keyframes hw-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
  @keyframes hw-bounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-4px); opacity: 1; } }
  @keyframes hw-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }

  /* Light-weight Markdown polish. We don't ship a parser — assistant content
     is inserted as text by default. A future pass can add a tiny renderer. */
`;
